import { useEffect, useState } from "react";
import { useQuery, useReactiveVar } from "@apollo/client";
import { connect } from "react-redux";
import { useParams } from "react-router-dom";

import {
  AlertTypes,
  ProViewAlertTypes,
  devicesAlerts,
  residentTransitionAlerts,
} from "../../../utils/alertUtils";
import {
  EOrganizationType,
  IAlarmNotificationSetting,
  IAlertNotificationsParams,
  IAppState,
  IOrganizationData,
  IOrganizationParams,
  IPropertyData,
  IRegionData,
  IUserDataWithAccountInfo,
} from "../../../interfaces/interfaces";
import {
  GET_ALERT_NOTIFICATION_SETTINGS,
  GET_INTEGRATION_TYPES_FOR_ORGANIZATION,
  GET_ORGANIZATION_BUILDINGS,
} from "../../../api/gqlQueries";
import {
  hasPMSIntegration,
  sortPropertiesByName,
} from "../../../utils/integrationUtils";
import {
  organizationIdNameVar,
  smallHeaderBackArrow,
  smallHeaderTitle,
} from "../../apollo/LocalState";
import { ReactComponent as Bell } from "../../../img/Bell.svg";
import Breadcrumbs from "../../breadcrumbs/Breadcrumbs";
import BuildingDropdown from "../../../utils/buildingDropdown";
import LearnMoreButton from "../../buttons/LearnMoreButton";
import { ReactComponent as Mail } from "../../../img/mail.svg";
import { NotificationMethod } from "../../../utils/notificationUtils";
import Spinner from "../../Spinner";
import { ReactComponent as TextMessage } from "../../../img/text-message.svg";

import GroupedAlertPreferences from "./GroupedAlertPreferences";

const AlertPreferencesPageBody: React.FC<{
  userId: string;
  propertyId: string;
  hasPMS: boolean;
}> = ({ userId, propertyId, hasPMS }) => {
  const organizationIdName = useReactiveVar(organizationIdNameVar);
  const { data: buildingData, loading } = useQuery<
    IUserDataWithAccountInfo,
    IAlertNotificationsParams
  >(GET_ALERT_NOTIFICATION_SETTINGS, { variables: { propertyId, userId } });
  if (!buildingData) return null;
  if (loading) return <Spinner />;
  const allAlarmNotificationSettings = Object.values(
    organizationIdName.organizationType ===
      EOrganizationType.PRO_VIEW_ORGANIZATION
      ? ProViewAlertTypes
      : AlertTypes
  )
    .filter((alertType) => {
      switch (alertType) {
        case AlertTypes.ExtremeTemperature:
        case AlertTypes.LeakDetected:
        case AlertTypes.ReconfigurationTakingTooLong:
        case AlertTypes.LowBattery:
        case AlertTypes.OfflineDevices:
          return true;
        case AlertTypes.ResidentInviteFailed:
          return hasPMS;
        default:
          return false;
      }
    })
    .map((alertType) => {
      const existingAlarmSetting =
        buildingData.user.alarmNotificationSettings.find(
          (element) => element.alarmType === alertType
        );
      if (!existingAlarmSetting)
        return {
          alarmType: alertType,
          notificationSettings: [
            { enabled: false, notificationMethod: NotificationMethod.Email },
            { enabled: false, notificationMethod: NotificationMethod.Sms },
          ],
        } as IAlarmNotificationSetting;
      return existingAlarmSetting;
    });
  const DevicesNotifications = allAlarmNotificationSettings.filter((setting) =>
    devicesAlerts.includes(setting.alarmType)
  );
  const residentTransitionNotifications = allAlarmNotificationSettings.filter(
    (setting) => residentTransitionAlerts.includes(setting.alarmType)
  );
  return (
    <>
      {DevicesNotifications.length > 0 && (
        <GroupedAlertPreferences
          name="Devices"
          propertyId={propertyId}
          alarmNotificationSettings={DevicesNotifications}
        />
      )}
      {residentTransitionNotifications.length > 0 && (
        <GroupedAlertPreferences
          name="Resident Transitions"
          propertyId={propertyId}
          alarmNotificationSettings={residentTransitionNotifications}
        />
      )}
    </>
  );
};

interface IOrganizationBuildings {
  organization: {
    buildings: IPropertyData[];
    regions: IRegionData[];
  };
}

export interface AlertPreferencesLandingPageProps {
  userId: string;
}

const AlertPreferencesLandingPage: React.FC<
  AlertPreferencesLandingPageProps
> = ({ userId }) => {
  const organizationIdName = useReactiveVar(organizationIdNameVar);
  const params = useParams();
  const { propertyId } = params;
  let { organizationId } = params;
  if (organizationId === undefined) {
    organizationId = "";
  }
  const [selectedBuildingId, setSelectedBuildingId] = useState<string>("");
  const { loading: organizationLoading, data: organizationData } = useQuery<
    IOrganizationBuildings,
    IOrganizationParams
  >(GET_ORGANIZATION_BUILDINGS, { variables: { organizationId } });
  const {
    loading: organizationIntegrationsLoading,
    data: organizationIntegrations,
  } = useQuery<IOrganizationData, IOrganizationParams>(
    GET_INTEGRATION_TYPES_FOR_ORGANIZATION,
    {
      variables: { organizationId },
    }
  );
  let sortedProperties: IPropertyData[] | IRegionData[] = [];
  if (organizationData) {
    const propertyData =
      organizationData.organization.buildings.length > 0
        ? organizationData.organization.buildings
        : organizationData.organization.regions;
    sortedProperties = sortPropertiesByName(propertyData);
  }

  useEffect(() => {
    smallHeaderTitle("Alert Preferences");
    smallHeaderBackArrow(true);
    return () => {
      smallHeaderTitle("");
      smallHeaderBackArrow(false);
    };
  }, []);
  useEffect(() => {
    if (sortedProperties.length !== 0 && !propertyId)
      setSelectedBuildingId(sortedProperties[0].id);
    if (sortedProperties.length !== 0 && propertyId)
      setSelectedBuildingId(propertyId);
  }, [organizationData]);
  if (organizationLoading || organizationIntegrationsLoading)
    return <Spinner />;
  if (!organizationData || !organizationIntegrations) return null;
  const hasPMS = hasPMSIntegration(
    organizationIntegrations.organization.integrations
  );
  const crumbs = [
    {
      name: organizationIdName.organizationName,
      path: `/${organizationIdName.organizationId}/organization`,
    },
    { name: "Alert Preferences", path: "" },
  ];
  return (
    <div className="grouped-preferences-main-container">
      <Breadcrumbs
        crumbs={crumbs}
        rootCrumb={{ name: "Organizations", path: "/" }}
      />
      <div className="grouped-preferences-main-header">
        <Bell className="grouped-preferences-main-header-svg" title="Bell" />
        <div className="unit-title">Alert Preferences</div>
      </div>
      <div className="grouped-preferences-main-link">
        <p>
          Manage how Brilliant notifies you about alerts. Changes will not
          affect alert preferences for other members in this organization.
        </p>
        <LearnMoreButton
          link="https://support.brilliant.tech/hc/en-us/articles/13499857157019"
          text="Learn More"
        />
      </div>
      <div className="grouped-preferences-building-container">
        {organizationIdName.organizationType !==
        EOrganizationType.PRO_VIEW_ORGANIZATION ? (
          <BuildingDropdown
            sortedBuildings={sortedProperties}
            setSelectedBuildingId={setSelectedBuildingId}
            selectedBuildingId={selectedBuildingId}
          />
        ) : (
          <div />
        )}
        <div className="grouped-preferences-building-icons">
          <div className="grouped-preferences-building-icon">
            <Mail />
            <div className="text-label">Email</div>
          </div>
          <div className="grouped-preferences-building-icon">
            <TextMessage />
            <div className="text-label">Text</div>
          </div>
        </div>
      </div>
      {selectedBuildingId && (
        <AlertPreferencesPageBody
          userId={userId}
          propertyId={selectedBuildingId}
          hasPMS={hasPMS}
        />
      )}
    </div>
  );
};

const mapStateToProps = (state: IAppState) => {
  return {
    userId: state.user.userId,
  };
};

export default connect(mapStateToProps)(AlertPreferencesLandingPage);
